// src/components/DashaTimeline.js
import React, { useMemo, useRef, useState } from "react";

const PLANET_HINDI = {
  Sun: "Surya",
  Moon: "Chandra",
  Mars: "Mangal",
  Mercury: "Budh",
  Jupiter: "Guru",
  Venus: "Shukra",
  Saturn: "Shani",
  Rahu: "Rahu",
  Ketu: "Ketu",
};

const formatPlanetLabel = (planet) => {
  const hi = PLANET_HINDI[planet];
  return hi ? `${planet} (${hi})` : planet;
};

// Role colors (same vibe as Remedies)
const ROLE_COLORS = {
  MD: {
    border: "rgba(245,158,11,0.65)",
    glow: "rgba(245,158,11,0.20)",
    label: "rgba(245,158,11,0.75)",
    bg: "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(15,23,42,0.85))",
  },
  AD: {
    border: "rgba(34,211,238,0.65)",
    glow: "rgba(34,211,238,0.18)",
    label: "rgba(34,211,238,0.75)",
    bg: "linear-gradient(135deg, rgba(34,211,238,0.14), rgba(15,23,42,0.85))",
  },
  PD: {
    border: "rgba(168,85,247,0.65)",
    glow: "rgba(168,85,247,0.18)",
    label: "rgba(168,85,247,0.75)",
    bg: "linear-gradient(135deg, rgba(168,85,247,0.14), rgba(15,23,42,0.85))",
  },
};

// Browser-based IST time (safe)
function getISTNowString() {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(now);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    const yyyy = get("year");
    const mm = get("month");
    const dd = get("day");
    const hh = get("hour");
    const mi = get("minute");

    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  } catch (e) {
    const d = new Date();
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

function DashaTimeline({ dasha }) {
  // ✅ HOOKS FIRST — ALWAYS
  const [showMdDetails, setShowMdDetails] = useState(false);
  const [showAdDetails, setShowAdDetails] = useState(true); // ✅ default open
  const [showPdDetails, setShowPdDetails] = useState(false);

  // ✅ Refs for scroll focus
  const mdRef = useRef(null);
  const adRef = useRef(null);
  const pdRef = useRef(null);

  // Use browser IST time (not backend)
  const istNow = useMemo(() => getISTNowString(), []);

  // Defensive after hooks
  if (!dasha) return null;

  const d = dasha;

  const hasMdTimeline = Array.isArray(d.md_timeline) && d.md_timeline.length > 0;
  const hasAdTimeline = Array.isArray(d.ad_timeline) && d.ad_timeline.length > 0;
  const hasPdTimeline = Array.isArray(d.pd_timeline) && d.pd_timeline.length > 0;

  const hasMD = !!d?.md?.planet;
  const hasAD = !!d?.ad?.planet;
  const hasPD = !!d?.pd?.planet;

  const scrollToRef = (ref) => {
    if (!ref?.current) return;
    ref.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // ✅ Open only one + scroll to it
  const openOnly = (key) => {
    if (key === "MD") {
      setShowMdDetails(true);
      setShowAdDetails(false);
      setShowPdDetails(false);

      setTimeout(() => scrollToRef(mdRef), 80);
    } else if (key === "AD") {
      setShowMdDetails(false);
      setShowAdDetails(true);
      setShowPdDetails(false);

      setTimeout(() => scrollToRef(adRef), 80);
    } else if (key === "PD") {
      setShowMdDetails(false);
      setShowAdDetails(false);
      setShowPdDetails(true);

      setTimeout(() => scrollToRef(pdRef), 80);
    }
  };

  const baseCard = {
    borderRadius: 12,
    border: "1px solid rgba(148,163,184,0.18)",
    background: "rgba(15,23,42,0.92)",
    boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
  };

  const sectionHeaderRow = {
    marginBottom: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  };

  const sectionButton = {
    padding: "5px 12px",
    borderRadius: 999,
    border: "1px solid rgba(75,85,99,0.9)",
    background: "transparent",
    color: "#e5e7eb",
    fontSize: 11,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const helperText = {
    fontSize: 12,
    color: "#9ca3af",
    marginBottom: 6,
    lineHeight: 1.55,
  };

  // Snapshot card builder
  const renderSnapshotCard = (roleKey, title, seg, subline) => {
    const cfg = ROLE_COLORS[roleKey];
    const isFocused =
      (roleKey === "AD" && showAdDetails) ||
      (roleKey === "MD" && showMdDetails) ||
      (roleKey === "PD" && showPdDetails);

    if (!seg?.planet) return null;

    return (
      <button
        type="button"
        onClick={() => openOnly(roleKey)}
        style={{
          textAlign: "left",
          width: "100%",
          padding: 12,
          borderRadius: 12,
          border: `1px solid ${isFocused ? cfg.border : "rgba(55,65,81,0.9)"}`,
          background: cfg.bg,
          cursor: "pointer",
          boxShadow: isFocused ? `0 0 0 3px ${cfg.glow}` : "none",
          transition: "all 0.15s ease",
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: cfg.label,
            marginBottom: 4,
            fontWeight: 800,
          }}
        >
          {title}
        </div>

        <div
          style={{
            fontWeight: 800,
            marginBottom: 4,
            color: "#f8fafc",
            fontSize: 14,
          }}
        >
          {formatPlanetLabel(seg.planet)}
        </div>

        <div style={{ fontSize: 12, color: "#e5e7eb" }}>
          {seg.start} → {seg.end}
        </div>

        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
          {subline}
        </div>

        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
          Tap to open details
        </div>
      </button>
    );
  };

  const TimelineLockBlock = ({ level }) => {
    const labelMap = {
      MD: "Mahadasha Timeline",
      AD: "Antardasha Timeline",
      PD: "Pratyantardasha Timeline",
    };

    return (
      <div
        style={{
          marginBottom: 14,
          padding: 18,
          borderRadius: 12,
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(148,163,184,0.15)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: "#64748b",
            marginBottom: 8,
          }}
        >
          Interpretive Depth
        </div>

        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          🔒 {labelMap[level]}
        </div>

        <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6 }}>
          This section expands how planetary periods unfold over time —
          showing transitions, sequences, and future progression.
          <br /><br />
          Timeline exploration is available in the Insight plan.
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontSize: 13 }}>
      {/* =====================================================
          TOP SNAPSHOT
      ====================================================== */}
      <div style={{ ...baseCard, marginBottom: 14, padding: 12 }}>
        <div style={sectionHeaderRow}>
          <div style={{ fontWeight: 800, color: "#f8fafc" }}>
            📌 Current Vimshottari Snapshot
          </div>

          <div style={{ fontSize: 11, color: "#9ca3af" }}>
            Today (IST): <span style={{ color: "#e5e7eb" }}>{istNow}</span>
          </div>
        </div>

        <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
          These three periods describe what is shaping your life right now.
          Mahadasha is the long background, Antardasha is the current theme, and
          Pratyantardasha is short-term intensity.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 10,
          }}
        >
          {hasMD
            ? renderSnapshotCard(
                "MD",
                "MAHADASHA (MD)",
                d.md,
                "Long-term background"
              )
            : null}

          {hasAD
            ? renderSnapshotCard(
                "AD",
                "ANTARDASHA (AD)",
                d.ad,
                "Current theme"
              )
            : null}

          {hasPD
            ? renderSnapshotCard(
                "PD",
                "PRATYANTARDASHA (PD)",
                d.pd,
                "Short-term intensity"
              )
            : null}
        </div>

        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10 }}>
          Birth Moon Nakshatra lord: <b>{d.birth_nakshatra_lord}</b>. Use this
          tab mainly to know <b>what is running now</b> and{" "}
          <b>what will start next</b>.
        </div>
      </div>

      {/* =====================================================
          MAHADASHA
      ====================================================== */}
      <div ref={mdRef}>
        <div style={sectionHeaderRow}>
          <div style={{ fontWeight: 700, color: "#f8fafc" }}>
            Mahadasha Timeline (birth → ~100 yrs)
          </div>
          <button
            onClick={() => {
              setShowMdDetails((v) => !v);
              setTimeout(() => scrollToRef(mdRef), 60);
            }}
            style={sectionButton}
          >
            {hasMdTimeline
              ? (showMdDetails ? "Hide MD timeline" : "Show MD timeline")
              : "View timeline (locked)"
            }
          </button>
        </div>

        <div style={helperText}>
          Open this only when you want to see{" "}
          <b>when the current Mahadasha ends</b> and{" "}
          <b>which Mahadasha comes after that</b>.
        </div>

        {showMdDetails && (
          hasMdTimeline ? (
            <div
              style={{
                overflowX: "auto",
                marginBottom: 14,
                borderRadius: 12,
                border: "1px solid rgba(55,65,81,0.9)",
                background: "rgba(2,6,23,0.65)",
              }}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr>
                    {["#", "Mahadasha", "Start", "End", "Status"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: 8,
                          borderBottom: "1px solid rgba(75,85,99,0.9)",
                          textAlign: "left",
                          color: "#cbd5e1",
                          fontWeight: 700,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(d.md_timeline || []).map((seg, idx) => {
                    const isCurrent = idx === d.md_current_index;
                    return (
                      <tr
                        key={idx}
                        style={{
                          background: isCurrent
                            ? "rgba(250,204,21,0.10)"
                            : "transparent",
                        }}
                      >
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {formatPlanetLabel(seg.planet)}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {seg.start}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {seg.end}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                            color: isCurrent ? "rgba(245,158,11,0.75)" : "#9ca3af",
                            fontSize: 11,
                            fontWeight: isCurrent ? 700 : 400,
                          }}
                        >
                          {isCurrent ? "● Current MD" : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        ) : (
          <TimelineLockBlock level="MD" />
        )
      )}
      </div>

      {/* =====================================================
          ANTARDASHA
      ====================================================== */}
      <div ref={adRef}>
        <div style={sectionHeaderRow}>
          <div style={{ fontWeight: 700, color: "#f8fafc" }}>
            Antardasha inside current Mahadasha
          </div>
          <button
            onClick={() => {
              setShowAdDetails((v) => !v);
              setTimeout(() => scrollToRef(adRef), 60);
            }}
            style={{
              ...sectionButton,
              border: showAdDetails
                ? `1px solid ${ROLE_COLORS.AD.border}`
                : sectionButton.border,
            }}
          >
            {hasAdTimeline
              ? (showAdDetails ? "Hide AD timeline" : "Show AD timeline")
              : "View timeline (locked)"
            }
          </button>
        </div>

        <div style={helperText}>
          This is the most useful section. It shows what’s running now and what
          starts next.
        </div>

        {showAdDetails && (
          hasAdTimeline ? (
            <div
              style={{
                overflowX: "auto",
                marginBottom: 14,
                borderRadius: 12,
                border: `1px solid rgba(34,211,238,0.35)`,
                background: "rgba(2,6,23,0.65)",
                boxShadow: "0 0 0 3px rgba(34,211,238,0.06)",
              }}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr>
                    {["#", "Antardasha", "Start", "End", "Status"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: 8,
                          borderBottom: "1px solid rgba(75,85,99,0.9)",
                          textAlign: "left",
                          color: "#cbd5e1",
                          fontWeight: 700,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(d.ad_timeline || []).map((seg, idx) => {
                    const isCurrent = idx === d.ad_current_index;
                    return (
                      <tr
                        key={idx}
                        style={{
                          background: isCurrent
                            ? "rgba(34,211,238,0.10)"
                            : "transparent",
                        }}
                      >
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {formatPlanetLabel(seg.planet)}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {seg.start}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                          }}
                        >
                          {seg.end}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderTop: "1px solid rgba(55,65,81,0.6)",
                            color: isCurrent ? "rgba(34,211,238,0.75)" : "#9ca3af",
                            fontSize: 11,
                            fontWeight: isCurrent ? 700 : 400,
                          }}
                        >
                          {isCurrent ? "● Current AD" : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        ) : (
          <TimelineLockBlock level="AD" />
        )
      )}
      </div>

      {/* =====================================================
          PRATYANTARDASHA
      ====================================================== */}
      <div ref={pdRef}>
        <div style={sectionHeaderRow}>
          <div style={{ fontWeight: 700, color: "#f8fafc" }}>
            Pratyantardasha inside current Antardasha
          </div>
          <button
            onClick={() => {
              setShowPdDetails((v) => !v);
              setTimeout(() => scrollToRef(pdRef), 60);
            }}
            style={sectionButton}
          >
            {hasPdTimeline
              ? (showPdDetails ? "Hide PD timeline" : "Show PD timeline")
              : "View timeline (locked)"
            }
          </button>
        </div>

        <div style={helperText}>
          Useful when you want a <b>month-to-month flavor</b> of events during
          the present Antardasha.
        </div>

        {showPdDetails && (
          hasPdTimeline ? (
          <div
            style={{
              overflowX: "auto",
              marginBottom: 4,
              borderRadius: 12,
              border: "1px solid rgba(55,65,81,0.9)",
              background: "rgba(2,6,23,0.65)",
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  {["#", "Pratyantardasha", "Start", "End", "Status"].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          padding: 8,
                          borderBottom: "1px solid rgba(75,85,99,0.9)",
                          textAlign: "left",
                          color: "#cbd5e1",
                          fontWeight: 700,
                        }}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {(d.pd_timeline || []).map((seg, idx) => {
                  const isCurrent = idx === d.pd_current_index;
                  return (
                    <tr
                      key={idx}
                      style={{
                        background: isCurrent
                          ? "rgba(168,85,247,0.10)"
                          : "transparent",
                      }}
                    >
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid rgba(55,65,81,0.6)",
                        }}
                      >
                        {idx + 1}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid rgba(55,65,81,0.6)",
                        }}
                      >
                        {formatPlanetLabel(seg.planet)}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid rgba(55,65,81,0.6)",
                        }}
                      >
                        {seg.start}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid rgba(55,65,81,0.6)",
                        }}
                      >
                        {seg.end}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid rgba(55,65,81,0.6)",
                          color: isCurrent ? "rgba(168,85,247,0.75)" : "#9ca3af",
                          fontSize: 11,
                          fontWeight: isCurrent ? 700 : 400,
                        }}
                      >
                        {isCurrent ? "● Current PD" : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <TimelineLockBlock level="PD" />
        )
      )}
      </div>
    </div>
  );
}

export default DashaTimeline;
